import { billingClient } from '../../billing-api-integration/client';
import { GraphQLQuery } from '@aws-amplify/api';
import { notifyError } from '../../../../components/ServiceNotifications';
import {
    createPaymentProfileMutation,
    deletePaymentProfileMutation,
    initializePaymentProfileSetupMutation,
    confirmPaymentProfileSetupMutation
} from './mutations';
import { PaymentProfile } from '../types';

type ListPaymentProfilesResponse = {
    listPaymentProfilesForCurrentCustomer: { items: PaymentProfile[]; nextToken?: string };
};

type CreatePaymentProfileResponse = { createPaymentProfile: PaymentProfile };
type DeletePaymentProfileResponse = { deletePaymentProfile: { id: string } };

type InitializePaymentProfileSetupResponse = {
    initializePaymentProfileSetup: {
        clientToken: string;
        providerCustomerId: string;
        sessionId: string;
    };
};

type ConfirmPaymentProfileSetupResponse = {
    confirmPaymentProfileSetup: PaymentProfile;
};

type CreatePaymentProfileInput = {
    customerId?: string;
    billingProviderId: string;
    providerPaymentMethodId: string;
    name?: string;
    status?: string;
};

const listPaymentProfilesForCurrentCustomer = `
   query listPaymentProfilesForCurrentCustomer($limit: Int, $nextToken: String) {
       listPaymentProfilesForCurrentCustomer(limit: $limit, nextToken: $nextToken) {
           items {
              id
              name
              customerId
              billingProviderId
              billingProvider {
                  id
                  key
                  name
              }
              providerPaymentMethodId
              status
           }
           nextToken
       }
   }
`;

export const fetchPaymentProfiles = async (): Promise<PaymentProfile[]> => {
    try {
        const response = await billingClient.graphql<GraphQLQuery<ListPaymentProfilesResponse>>({
            query: listPaymentProfilesForCurrentCustomer
        });
        const profiles = response.data?.listPaymentProfilesForCurrentCustomer?.items || [];
        
        // TODO: Billing API should return payment method details
        // Remove direct Stripe API calls once Billing API implements this
        return profiles;
    } catch (e) {
        console.trace(e);
        notifyError(e);
        return [];
    }
};

export const createPaymentProfile = async (input: CreatePaymentProfileInput) => {
    try {
        const response = await billingClient.graphql<GraphQLQuery<CreatePaymentProfileResponse>>({
            query: createPaymentProfileMutation,
            variables: { input }
        });
        return response.data?.createPaymentProfile;
    } catch (e) {
        console.trace(e);
        notifyError(e);
        throw e;
    }
};

export const deletePaymentProfile = async (id: string) => {
    try {
        await billingClient.graphql<GraphQLQuery<DeletePaymentProfileResponse>>({
            query: deletePaymentProfileMutation,
            variables: { input: { id } }
        });
    } catch (e) {
        console.trace(e);
        notifyError(e);
        throw e;
    }
};

export const initializePaymentProfileSetup = async (billingProviderId: string) => {
    try {
        const response = await billingClient.graphql<GraphQLQuery<InitializePaymentProfileSetupResponse>>({
            query: initializePaymentProfileSetupMutation,
            variables: { input: { billingProviderId } }
        });
        return response.data?.initializePaymentProfileSetup;
    } catch (e) {
        console.trace(e);
        notifyError(e);
        throw e;
    }
};

export const confirmPaymentProfileSetup = async (input: {
    billingProviderId: string;
    sessionId: string;
    providerPaymentMethodId?: string;
}) => {
    try {
        const response = await billingClient.graphql<GraphQLQuery<ConfirmPaymentProfileSetupResponse>>({
            query: confirmPaymentProfileSetupMutation,
            variables: { input }
        });
        return response.data?.confirmPaymentProfileSetup;
    } catch (e) {
        console.trace(e);
        notifyError(e);
        throw e;
    }
};
